import dayjs from "dayjs";
import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";

import CardProjectDetail from "~/components/molecules/CardProjectDetail";
import Section from "~/components/templates/SectionProject";
import { IProject } from "~/entities/project";
import { TENURE_ITEM } from "~/utilities/constants/tenureItems";

export interface SectionProjectDetailsProps {
  className?: string;
  id: string;
  title: string;
  project: IProject;
}

export type Ref = HTMLDivElement;

const SectionProjectDetails = forwardRef<Ref, SectionProjectDetailsProps>(
  ({ className = "", id, title, project }, ref) => {
    const { t } = useTranslation();

    const {
      location,
      address,
      developer,
      totalUnits,
      manualTotalUnitsCount,
      tenure,
      expectedTop,
      category,
    } = project;

    const projectDescription = useMemo(
      () => ({
        location: location?.name ?? t("not_available.location"),
        address: address ?? t("not_available.address"),
        developer: developer?.name ?? t("not_available.developer"),
        totalUnits:
          manualTotalUnitsCount > 0
            ? manualTotalUnitsCount.toString()
            : totalUnits > 0
            ? totalUnits.toString()
            : t("not_available.units"),
        tenure: TENURE_ITEM[tenure]?.name ?? t("not_available.tenure"),
        expectedTop: expectedTop
          ? dayjs(expectedTop).format("DD MMMM YYYY")
          : t("not_available.top"),
        projectCategory: category?.name ?? t("not_available.category"),
        googleMapUrl: "", // Intentionally left blank, can be updated as needed
      }),
      [
        location?.name,
        t,
        address,
        developer?.name,
        totalUnits,
        manualTotalUnitsCount,
        tenure,
        expectedTop,
        category?.name,
      ]
    );

    if (!project) return null;

    return (
      <Section ref={ref} id={id} className={className}>
        <CardProjectDetail
          projectName={t(title)}
          projectDescription={projectDescription}
        />
      </Section>
    );
  }
);

SectionProjectDetails.displayName = "SectionProjectDetails";

export default SectionProjectDetails;
